/* Tee Zee bundle upsell — works with Dawn 15 (cart drawer, cart notification or cart page).
   deal 'pair':  2nd glove discounted, optional 3-pack with the 3rd discounted (Buy X get Y).  deal 'order': add any item, money off the order. */
(() => {
  if (customElements.get('tzb-bundle')) return;

  const esc = (s) =>
    String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
  const isDefault = (p) => p.variants.length === 1 && p.variants[0].t === 'Default Title';

  class TzbBundle extends HTMLElement {
    connectedCallback() {
      if (this.cfg) return;
      this.cfg = JSON.parse(this.querySelector('[data-tzb-config]').textContent);
      // Don't advertise a free gift that can't be shipped.
      if (this.cfg.gift && !this.cfg.gift.variants.some((v) => v.a)) this.cfg.gift = null;
      this.mount = this.querySelector('.tzb__mount');
      this.money = new Intl.NumberFormat(this.cfg.locale || undefined, {
        style: 'currency',
        currency: this.cfg.currency || 'USD',
      });

      this.byHandle = new Map(this.cfg.products.map((p) => [p.handle, p]));
      this.variants = new Map();
      this.cfg.products.forEach((p) => p.variants.forEach((v) => this.variants.set(v.id, { p, v })));
      this.current = this.cfg.products[0];

      // Dawn's product form for this product (holds the selected variant id).
      this.scope = this.closest('product-info') || this.closest('section') || document;
      this.form =
        this.scope.querySelector('form[data-type="add-to-cart-form"]') ||
        this.scope.querySelector('form[action*="/cart/add"]');
      const idInput = this.form?.querySelector('input[name="id"]');
      const urlVariant = Number(new URL(location.href).searchParams.get('variant'));
      this.curId = this.pickId(this.current, Number(idInput?.value) || urlVariant);

      // 'pair' starts the 2nd slot on this same product; 'order' suggests a different in-stock product.
      this.isOrder = this.cfg.deal === 'order';
      const inStock = (p) => p.variants.some((v) => v.a);
      const second = this.isOrder
        ? this.byHandle.get(this.cfg.suggest) || this.cfg.products.slice(1).find(inStock) || this.current
        : this.current;
      const sameSize = this.variant(this.curId)?.t;
      // The 3-pack tier only exists when the render tag sets third_pair_amount.
      this.hasThree = !this.isOrder && this.cfg.third > 0;

      this.state = {
        tier: 2,
        pairs: [
          { handle: this.current.handle, id: this.curId, touched: false },
          { handle: second.handle, id: this.pickId(second, this.curId, sameSize), touched: false },
          ...(this.hasThree ? [{ handle: this.current.handle, id: this.curId, touched: false }] : []),
        ],
        giftId: this.cfg.gift ? this.pickId(this.cfg.gift) : null,
        busy: false,
        error: '',
      };

      if (this.dataset.hideThemeButtons === 'true' && this.scope !== document) {
        this.scope.classList.add('tzb-hide-theme-buttons');
      }

      this.listenToTheme();
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('change', this.onChange.bind(this));
      this.render();
    }

    disconnectedCallback() {
      this.unsubscribe?.();
    }

    /* ---------- data helpers ---------- */

    // A valid variant id of product p: the preferred one if it belongs to p,
    // else one with the same title as `likeTitle`, else the first available.
    pickId(p, preferredId, likeTitle) {
      if (preferredId && p.variants.some((v) => v.id === preferredId)) return preferredId;
      const same = likeTitle && p.variants.find((v) => v.a && v.t === likeTitle);
      return (same || p.variants.find((v) => v.a) || p.variants[0]).id;
    }

    variant(id) {
      return this.variants.get(id)?.v || this.cfg.gift?.variants.find((v) => v.id === id);
    }

    // Prices for the 2-pack (tier 2) or 3-pack (tier 3). Only one discount applies: a 3-pack is
    // two pairs at full price plus the 3rd pair's discount, not the 2nd pair's discount as well.
    totals(tier) {
      const prices = this.state.pairs.slice(0, tier).map((pair) => this.variant(pair.id)?.c || 0);
      const sum = prices.reduce((a, b) => a + b, 0);
      // 'order': money off the whole order. 'pair': Shopify's Buy X Get Y always reduces the cheaper item.
      const base = this.isOrder ? sum : Math.min(...prices);
      const amount = tier === 3 ? this.cfg.third : this.cfg.amount;
      const off = Math.min(base, amount ? this.amountCents(amount) : Math.round((base * this.cfg.pct) / 100));
      // A $0 gift product shows its compare-at price as the value being given away.
      const giftV = this.state.giftId ? this.variant(this.state.giftId) : null;
      const gift = giftV ? Math.max(giftV.c || 0, giftV.cc || 0) : 0;
      return { compare: sum + gift, total: sum - off };
    }

    fmt(cents) {
      return this.money.format(cents / 100);
    }

    // Fixed discount in the shopper's currency (Shopify converts it the same way for Markets).
    amountCents(amount) {
      return Math.round(amount * 100 * (Number(window.Shopify?.currency?.rate) || 1));
    }

    // "$20" or "30%"
    offLabel(tier) {
      const amount = tier === 3 ? this.cfg.third : this.cfg.amount;
      if (!amount) return `${this.cfg.pct}%`;
      const cents = this.amountCents(amount);
      return cents % 100 ? this.fmt(cents) : this.fmt(cents).replace(/[.,]00(?=\D*$)/, '');
    }

    // All wording that differs between the two deals.
    copy(tier = this.state.tier) {
      const off = this.offLabel(tier);
      const count = tier === 3 ? 3 : 2;
      const nth = tier === 3 ? '3rd' : '2nd';
      return this.isOrder
        ? {
            single: 'Just this item',
            bundle: 'Add any item',
            bundleSub: 'Shorts, rash guards, gloves &amp; more',
            offerSmall: 'your whole<br>order',
            label: (i) => (i === 0 ? 'This item' : `Add-on · <b>${off} off</b>`),
            pickTitle: () => `Add any item · ${off} off`,
            note: `${off} off your order`,
            prop: 'Add-on deal',
          }
        : {
            single: '1 pair',
            bundle: `${count} pairs pack`,
            bundleSub: 'Mix &amp; match any gloves',
            offerSmall: `your<br>${nth} pair`,
            label: (i) => (i === count - 1 ? `Pair ${i + 1} · <b>${off} off</b>` : `Pair ${i + 1}`),
            pickTitle: (i) => `Pick pair ${i + 1}${i === count - 1 ? ` · ${off} off` : ''}`,
            note: `${off} off the ${nth} pair`,
            prop: `${count}-pair pack`,
          };
    }

    /* ---------- keep pair 1 in sync with Dawn's variant picker ---------- */

    // Size chosen for this product (from Dawn's picker or the 1-pair size chips).
    // Untouched bundle pairs follow it.
    setCurrent(id) {
      if (!id || !this.variants.has(id) || this.variants.get(id).p !== this.current) {
        this.curId = null; // unavailable option combination
      } else {
        this.curId = id;
      }
      const [p1, ...rest] = this.state.pairs;
      const title = this.variant(this.curId)?.t;
      if (this.curId && !p1.touched && p1.handle === this.current.handle) p1.id = this.curId;
      // The other items follow the size too ('order' deal: matched by size name, e.g. M → M).
      rest.forEach((pair) => {
        if (this.curId && !pair.touched && (pair.handle === this.current.handle || this.isOrder)) {
          pair.id = this.pickId(this.byHandle.get(pair.handle), null, title);
        }
      });
      this.render();
    }

    listenToTheme() {
      const onVariant = (id) => this.setCurrent(id);

      if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS?.variantChange) {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (e) => onVariant(e?.data?.variant?.id));
      } else {
        this.form?.addEventListener('change', (e) => {
          if (e.target.name === 'id') onVariant(Number(e.target.value));
        });
      }
    }

    /* ---------- events ---------- */

    onClick(e) {
      const el = e.target.closest('[data-action]');
      if (!el) return;
      const { action } = el.dataset;

      if (action === 'tier') {
        this.state.tier = Number(el.dataset.tier);
        this.state.error = '';
        this.render();
      } else if (action === 'size') {
        this.setCurrent(Number(el.dataset.id));
      } else if (action === 'choose') {
        this.openPicker(Number(el.dataset.pair));
      } else if (action === 'pick') {
        const pair = this.state.pairs[this.pickerFor];
        const p = this.byHandle.get(el.dataset.handle);
        const other = this.state.pairs[this.pickerFor === 0 ? 1 : 0];
        Object.assign(pair, { handle: p.handle, id: this.pickId(p, null, this.variant(other.id)?.t), touched: true });
        this.dialog.close();
        this.render();
      } else if (action === 'close') {
        this.dialog.close();
      } else if (action === 'add') {
        this.addToCart();
      }
    }

    onChange(e) {
      const el = e.target;
      if (el.dataset.pairSelect !== undefined) {
        const pair = this.state.pairs[Number(el.dataset.pairSelect)];
        Object.assign(pair, { id: Number(el.value), touched: true });
      } else if (el.dataset.giftSelect !== undefined) {
        this.state.giftId = Number(el.value);
      }
      this.render();
    }

    /* ---------- rendering ---------- */

    sizeSelect(p, selectedId, attr) {
      if (isDefault(p)) return '';
      const opts = p.variants
        .map(
          (v) =>
            `<option value="${v.id}" ${v.id === selectedId ? 'selected' : ''} ${v.a ? '' : 'disabled'}>${esc(v.t)}${v.a ? '' : ' — sold out'}</option>`
        )
        .join('');
      return `<select class="tzb__select" ${attr} aria-label="Size">${opts}</select>`;
    }

    // Size buttons shown inside the 1-pair card
    sizeChips() {
      if (isDefault(this.current)) return '';
      const chips = this.current.variants
        .map(
          (v) =>
            `<button type="button" class="tzb__chip" data-action="size" data-id="${v.id}" aria-pressed="${v.id === this.curId}" ${v.a ? '' : 'disabled'}>${esc(v.t)}</button>`
        )
        .join('');
      return `
        <div class="tzb__sizes" role="group" aria-label="Size">
          <span class="tzb__eyebrow">Size</span>
          <div class="tzb__chips">${chips}</div>
        </div>`;
    }

    pairRow(i) {
      const pair = this.state.pairs[i];
      const p = this.byHandle.get(pair.handle);
      const v = this.variant(pair.id);
      const label = this.copy().label(i);
      // In the 'order' deal the first item is always this product.
      const canChange = !(this.isOrder && i === 0);
      return `
        <div class="tzb__row">
          <img class="tzb__thumb" src="${esc(v?.img || p.img || '')}" alt="" loading="lazy" width="48" height="48">
          <div class="tzb__row-text">
            <span class="tzb__eyebrow">${label}</span>
            <span class="tzb__row-title">${esc(p.title)}</span>
            ${this.sizeSelect(p, pair.id, `data-pair-select="${i}"`)}
            ${v && !v.a ? '<span class="tzb__soldout">Sold out — pick another size</span>' : ''}
          </div>
          ${canChange ? `<button type="button" class="tzb__choose" data-action="choose" data-pair="${i}">Change</button>` : ''}
        </div>`;
    }

    giftRow() {
      const g = this.cfg.gift;
      if (!g) return '';
      const v = this.variant(this.state.giftId);
      return `
        <div class="tzb__row">
          <img class="tzb__thumb" src="${esc(v?.img || g.img || '')}" alt="" loading="lazy" width="48" height="48">
          <div class="tzb__row-text">
            <span class="tzb__eyebrow"><b>Free</b> gift</span>
            <span class="tzb__row-title">${esc(g.title)}</span>
            ${this.sizeSelect(g, this.state.giftId, 'data-gift-select')}
          </div>
        </div>`;
    }

    perks() {
      return [this.cfg.gift && `Free ${esc(this.cfg.gift.title)}`, this.cfg.freeShipping && 'Free shipping'].filter(Boolean);
    }

    // The 2-pack (tier 2) or 3-pack (tier 3) card. The badge goes on the biggest pack on offer.
    bundleCard(tier) {
      const selected = this.state.tier === tier;
      const t = this.totals(tier);
      const c = this.copy(tier);
      const perks = this.perks();
      const badge = this.cfg.badge && tier === (this.hasThree ? 3 : 2);
      const rows = this.state.pairs.slice(0, tier).map((_, i) => this.pairRow(i)).join('');
      return `
          <div class="tzb__bundle" data-selected="${selected}">
            ${badge ? `<span class="tzb__badge">${esc(this.cfg.badge)}</span>` : ''}
            <button type="button" class="tzb__tier tzb__tier--bundle ${badge ? 'tzb__tier--badged' : ''}" role="radio" aria-checked="${selected}" data-action="tier" data-tier="${tier}">
              <span class="tzb__radio"></span>
              <span class="tzb__tier-head">
                <span class="tzb__tier-title">${c.bundle}</span>
                <span class="tzb__tier-sub">${c.bundleSub}</span>
              </span>
              <span class="tzb__tier-price">
                ${t.compare > t.total ? `<s>${this.fmt(t.compare)}</s>` : ''}
                ${this.fmt(t.total)}
                ${t.compare > t.total ? `<span class="tzb__save">Save ${this.fmt(t.compare - t.total)}</span>` : ''}
              </span>
              <span class="tzb__offer">
                <span class="tzb__offer-big">${this.offLabel(tier)} off</span>
                <span class="tzb__offer-small">${c.offerSmall}</span>
              </span>
              ${perks.length ? `<span class="tzb__perks">${perks.map((p) => `<span>+ ${p}</span>`).join('')}</span>` : ''}
            </button>
            ${selected ? `<div class="tzb__rows">${rows}${this.giftRow()}</div>` : ''}
          </div>`;
    }

    render() {
      const { tier, pairs, busy, error } = this.state;
      const single = this.variant(this.curId)?.c || 0;
      const perks = this.perks();

      const singleOk = !!this.variant(this.curId)?.a;
      const bundleOk = pairs.slice(0, tier).every((pair) => this.variant(pair.id)?.a);
      const canAdd = tier === 1 ? singleOk : bundleOk;
      const btnLabel = !canAdd ? 'Sold out' : tier === 1 ? 'Add to cart' : `Add ${tier} to cart`;
      const btnPrice = tier === 1 ? single : this.totals(tier).total;

      const c = this.copy();
      const note = [c.note, ...perks.map((p) => p.replace(/^Free/, 'free'))].join(' + ');

      this.mount.innerHTML = `
        <div class="tzb__tiers" role="radiogroup" aria-label="Choose a deal">
          <div class="tzb__single" data-selected="${tier === 1}">
            <button type="button" class="tzb__tier tzb__tier--single" role="radio" aria-checked="${tier === 1}" data-action="tier" data-tier="1">
              <span class="tzb__radio"></span>
              <span class="tzb__tier-head">
                <span class="tzb__tier-title">${c.single}</span>
                <span class="tzb__tier-sub">${esc(this.cfg.singleNote)}</span>
              </span>
              <span class="tzb__tier-price">${this.fmt(single)}</span>
            </button>
            ${tier === 1 ? this.sizeChips() : ''}
          </div>

          ${this.bundleCard(2)}
          ${this.hasThree ? this.bundleCard(3) : ''}
        </div>

        <button type="button" class="tzb__atc" data-action="add" ${canAdd && !busy ? '' : 'disabled'} aria-busy="${busy}">
          <span>${busy ? 'Adding…' : btnLabel}</span>
          ${canAdd ? `<span class="tzb__atc-price">${this.fmt(btnPrice)}</span>` : ''}
        </button>
        ${error ? `<p class="tzb__error" role="alert">${esc(error)}</p>` : ''}
        ${tier > 1 ? `<p class="tzb__note">${esc(note[0].toUpperCase() + note.slice(1))}, applied automatically at checkout.</p>` : ''}
      `;
    }

    /* ---------- glove picker ---------- */

    openPicker(pairIndex) {
      this.pickerFor = pairIndex;
      if (!this.dialog) {
        this.dialog = document.createElement('dialog');
        this.dialog.className = 'tzb-dialog';
        this.dialog.addEventListener('click', (e) => {
          if (e.target === this.dialog) this.dialog.close(); // backdrop click
        });
        // Search box: hide cards whose title doesn't match.
        this.dialog.addEventListener('input', (e) => {
          if (!e.target.matches('.tzb-dialog__search')) return;
          const q = e.target.value.trim().toLowerCase();
          let shown = 0;
          this.dialog.querySelectorAll('.tzb-card').forEach((card) => {
            const hit = !q || card.dataset.title.includes(q);
            card.hidden = !hit;
            if (hit) shown++;
          });
          this.dialog.querySelector('.tzb-dialog__empty').hidden = shown > 0;
        });
        this.appendChild(this.dialog);
      }
      const selected = this.state.pairs[pairIndex].handle;
      const inStock = (p) => p.variants.some((v) => v.a);
      // In the 'order' deal the add-on can't be this same product.
      const pool = this.cfg.products.filter((p) => !(this.isOrder && p === this.current));
      const cards = [...pool.filter(inStock), ...pool.filter((p) => !inStock(p))]
        .map((p) => {
          const available = inStock(p);
          const from = Math.min(...p.variants.map((v) => v.c));
          return `
            <button type="button" class="tzb-card" data-action="pick" data-handle="${esc(p.handle)}"
              data-title="${esc(p.title.toLowerCase())}"
              ${available ? '' : 'disabled'} aria-pressed="${p.handle === selected}">
              <img src="${esc(p.img || '')}" alt="" loading="lazy" width="150" height="150">
              <span class="tzb-card__title">${esc(p.title)}</span>
              <span class="tzb-card__price">${available ? this.fmt(from) : 'Sold out'}</span>
            </button>`;
        })
        .join('');
      this.dialog.innerHTML = `
        <div class="tzb-dialog__head">
          <h3>${this.copy().pickTitle(pairIndex)}</h3>
          <button type="button" class="tzb-dialog__close" data-action="close" aria-label="Close">&times;</button>
          ${pool.length > 8 ? `<input type="search" class="tzb-dialog__search" placeholder="${this.isOrder ? 'Search shorts, rash guards, gloves…' : 'Search gloves…'}" aria-label="Search products">` : ''}
        </div>
        <div class="tzb-dialog__grid">${cards}</div>
        <p class="tzb-dialog__empty" hidden>Nothing matches that search.</p>`;
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
    }

    /* ---------- cart ---------- */

    async addToCart() {
      const { tier, pairs, giftId } = this.state;
      const props = { _bundle: this.copy().prop };
      const items =
        tier === 1
          ? [{ id: this.curId, quantity: 1 }]
          : [
              ...pairs.slice(0, tier).map((pair) => ({ id: pair.id, quantity: 1, properties: props })),
              ...(giftId && this.variant(giftId)?.a ? [{ id: giftId, quantity: 1, properties: props }] : []),
            ];

      const cartUi = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
      const body = { items };
      if (cartUi?.getSectionsToRender) {
        body.sections = cartUi.getSectionsToRender().map((s) => s.id);
        body.sections_url = location.pathname;
      }

      this.state.busy = true;
      this.state.error = '';
      this.render();
      try {
        const res = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!res.ok || data.status) throw new Error(data.description || data.message || 'Could not add to cart');

        if (typeof publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'tzb-bundle', productVariantId: items[0].id, cartData: data });
        }
        if (cartUi?.renderContents) {
          cartUi.classList.remove('is-empty');
          cartUi.renderContents({ ...data, key: data.items[0].key, id: data.items[0].variant_id });
        } else {
          location.href = window.routes?.cart_url || '/cart';
        }
      } catch (err) {
        this.state.error = err.message;
      } finally {
        this.state.busy = false;
        this.render();
      }
    }
  }

  customElements.define('tzb-bundle', TzbBundle);
})();
